import { 
  ForgotPasswordRequest, 
  ForgotPasswordResponse, 
  ResetPasswordRequest, 
  SimplifiedUser, 
  UpdatePhoneNumber,
  UpdateEmail,
  UpdatePassword,
  User, 
  UserLogin, 
  UserRegister,
  SimplifiedUserInfo,
  OperatorRegister,
  InStoreUserRegister
} from "../../Models/User";
import { PagedResult } from "../../Models/PagedResult";
import { VerifyCodeRequest } from "../../Models/VerificationCode";
import { RegisterInStoreResponse } from "../../Types/RegisterInStoreResponseType";
import { CustomerImportResult } from "../../Models/User";
import api from "../axios";

export const accountService = {
  get: async (): Promise<User> => {
    const response = await api.get('/account');
    return response.data;
  },

  getOperators: async (): Promise<SimplifiedUserInfo[]> => {
    const response = await api.get('/account/operators');
    return response.data;
  },

  getUsers: async (page = 1, pageSize = 100, search?: string): Promise<PagedResult<SimplifiedUserInfo>> => {
    const response = await api.get('/account/users', {
      params: {
        page,
        pageSize,
        ...(search ? { search } : {})
      }
    });
    return response.data;
  },

  updatePhoneNumber: async (data: UpdatePhoneNumber): Promise<boolean> => {
    const response = await api.patch('/account/update-phone-number', data);
    return response.data;
  },

  updateEmail: async (data: UpdateEmail): Promise<boolean> => {
    const response = await api.patch('/account/update-email', data);
    return response.data;
  },

  updatePassword: async (data: UpdatePassword): Promise<boolean> => {
    const response = await api.patch('/account/update-password', data);
    return response.data;
  },

  googleLogin: async (idToken: string, accessToken?: string): Promise<{ user: SimplifiedUser | null; message?: string }> => {
    try {
      const response = await api.post('/account/login/google', { idToken, accessToken }, { skipAuthInterceptor: true });
      return { user: response.data };
    } catch (err: any) {
      if (err.response?.data) {
        return { user: null, message: err.response.data as string };
      }
      return { user: null, message: "Algo deu errado. Tente novamente." };
    }
  },

  login: async (data: UserLogin): Promise<{ user: SimplifiedUser | null; message?: string }> => {
    try {
      const response = await api.post('/account/login', data, { skipAuthInterceptor: true });
      return { user: response.data };
    } catch (err: any) {
      if (err.response?.data) {
        return { user: null, message: err.response.data as string };
      }
      return { user: null, message: "Algo deu errado. Tente novamente." };
    }
  },

  register: async (data: UserRegister): Promise<string> => {
    const response = await api.post('/account/register', data);
    return response.data;
  },

  registerInStore: async (data: InStoreUserRegister): Promise<RegisterInStoreResponse> => {
    const response = await api.post<RegisterInStoreResponse>('/account/register/in-store', data);
    return response.data;
  },

  importCustomers: async (file: File): Promise<CustomerImportResult> => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post<CustomerImportResult>('/account/import-customers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    return response.data;
  },

  completeContactInfo: async (data: { email?: string | null; phoneNumber?: string | null; customerType?: number | null; cpf?: string | null; cnpj?: string | null }): Promise<SimplifiedUser> => {
    const response = await api.patch('/account/complete-contact', data);
    return response.data as SimplifiedUser;
  },

  createOperator: async (data: OperatorRegister): Promise<string> => {
    const response = await api.post('/account/create-operator', data);
    return response.data;
  },

  setOperatorStatus: async (id: string, isActive: boolean): Promise<boolean> => {
    const response = await api.post(`/account/set-operator-status/${id}?isActive=${isActive}`);
    return response.data;
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    const response = await api.post('/account/forgot-password', data);
    return response.data;
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<boolean> => {
    const response = await api.post('/account/reset-password', data);
    return response.data;
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/account/logout', undefined, { skipAuthInterceptor: true });
    } catch {
      // Ignore logout errors; session will still be cleared on the client.
    }
  },

  sendVerificationCode: async (): Promise<string | null> => {
    const response = await api.post('/account/send-verification-code');
    return response.data;
  },

  verifyCode: async (data: VerifyCodeRequest): Promise<boolean> => {
    const response = await api.post('/account/verify-code', data);
    return response.data;
  },

  deleteAccount: async (): Promise<boolean> => {
    const response = await api.post('/account/delete-account');
    return response.data;
  }
};
